/**
 * Auth-hardening API calls (Wave 3, agent W12) — MFA enrolment, the devices
 * list, and change-password.
 *
 * Deliberately routed through `apiFetch` (bearer + one-401-rotation, from
 * `@/lib/api/client`) rather than through `AuthSession` directly: every one
 * of these calls happens from an ALREADY-authenticated screen (`/preferences`
 * or wherever `SecurityPage` mounts), which is exactly what `apiFetch` is
 * for. The one exception — completing an MFA challenge DURING login, before
 * a session exists — is `AuthSession.completeMfaLogin` in `lib/auth/
 * session.ts`, not here.
 *
 * Endpoints, per the W11 contract this page was built against:
 *   MFA enrolment  — POST/GET `/py-api/v1/auth/mfa/{enroll,confirm,disable,status}`
 *   Devices        — GET/DELETE `/py-api/v1/auth/devices[/{id}]`,
 *                     POST `/py-api/v1/auth/devices/revoke-all-others`
 *   Password       — POST `/py-api/v1/auth/change-password`
 *
 * `mfa` and the `auth` device/password routes are NOT the same domain root
 * (`/mfa` vs `/auth`) — see the comment on the `mfa` entry in
 * `frontend/core/src/api.ts` for why enrolment lives at the top level while
 * everything else here hangs off `/auth`.
 */
import { apiFetch } from "@/lib/api/client";
import { domainBaseUrl } from "@/lib/api/config";

const MFA_BASE = domainBaseUrl("mfa");
const AUTH_BASE = domainBaseUrl("auth");

export class SecurityApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "SecurityApiError";
    this.status = status;
  }
}

async function readErrorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string; message?: string; detail?: string };
    return body.error || body.message || (typeof body.detail === "string" ? body.detail : fallback);
  } catch {
    return fallback;
  }
}

async function asJson<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    throw new SecurityApiError(await readErrorMessage(res, fallback), res.status);
  }
  return (await res.json()) as T;
}

/* -------------------------------------------------------------------------
 * MFA enrolment
 * ---------------------------------------------------------------------- */

export interface MfaStatus {
  enabled: boolean;
  confirmedAt: string | null;
}

export interface MfaEnrolment {
  otpauthUri: string;
  secret: string;
}

export interface MfaRecoveryCodes {
  recoveryCodes: string[];
}

export function getMfaStatus(): Promise<MfaStatus> {
  return apiFetch(`${MFA_BASE}/status`, { headers: { accept: "application/json" } }).then((res) =>
    asJson<MfaStatus>(res, "Could not load your two-factor status."),
  );
}

export function enrolMfa(): Promise<MfaEnrolment> {
  return apiFetch(`${MFA_BASE}/enroll`, { method: "POST" }).then((res) =>
    asJson<MfaEnrolment>(res, "Could not start two-factor enrolment."),
  );
}

/** Confirms enrolment with a TOTP code. Recovery codes are returned ONCE — the
 *  caller must show them immediately and never re-fetch them. */
export function confirmMfa(code: string): Promise<MfaRecoveryCodes> {
  return apiFetch(`${MFA_BASE}/confirm`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ code }),
  }).then((res) => asJson<MfaRecoveryCodes>(res, "That code didn't match. Try again."));
}

export async function disableMfa(code: string): Promise<void> {
  const res = await apiFetch(`${MFA_BASE}/disable`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    throw new SecurityApiError(
      await readErrorMessage(res, "That code didn't match. Two-factor is still on."),
      res.status,
    );
  }
}

/* -------------------------------------------------------------------------
 * Devices
 * ---------------------------------------------------------------------- */

export interface DeviceRow {
  deviceId: string;
  deviceName: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  current: boolean;
}

export function listDevices(): Promise<DeviceRow[]> {
  return apiFetch(`${AUTH_BASE}/devices`, { headers: { accept: "application/json" } }).then((res) =>
    asJson<DeviceRow[]>(res, "Could not load your signed-in devices."),
  );
}

export async function revokeDevice(deviceId: string): Promise<void> {
  const res = await apiFetch(`${AUTH_BASE}/devices/${encodeURIComponent(deviceId)}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw new SecurityApiError(await readErrorMessage(res, "Could not sign out that device."), res.status);
  }
}

export async function revokeAllOtherDevices(): Promise<void> {
  const res = await apiFetch(`${AUTH_BASE}/devices/revoke-all-others`, { method: "POST" });
  if (!res.ok) {
    throw new SecurityApiError(
      await readErrorMessage(res, "Could not sign out your other devices."),
      res.status,
    );
  }
}

/* -------------------------------------------------------------------------
 * Change password
 * ---------------------------------------------------------------------- */

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const res = await apiFetch(`${AUTH_BASE}/password/change`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (!res.ok) {
    throw new SecurityApiError(
      await readErrorMessage(res, "Could not change your password. Check your current password and try again."),
      res.status,
    );
  }
}
